import { type PropsWithChildren } from 'react';

import { isCustomColorPalette } from '../../core/types/color-palette.js';
import { isColoredRangeArray } from '../../core/types/colored-range.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import { getValidColor } from '../../core/utils/getValidColor.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { safeGet } from '../../core/utils/safe-get.js';
import { XYChartDataContext } from '../context/XYChartData.context.js';
import { XYChartDotSeriesDataContext } from '../context/XYChartDotSeriesDataContext.js';
import { XYChartRectDataContext } from '../context/XYChartRectData.context.js';
import { useAxesConfig } from '../hooks/useAxesConfig.js';
import { useColorPalette } from '../hooks/useColorPalette.js';
import type {
  AxisIdToScaleDomain,
  DotSeriesToMetadata,
  DotSeriesWithDatapoints,
  RectSeriesIdToValueScaleDomain,
  RectSeriesToMetadata,
  RectSeriesWithDatapoints,
  XYChartVariantsInternal,
  WeakMapDatapoints,
  DatapointMetadata,
} from '../types/xy-chart-internals.js';
import { TIME_SCALE_TYPE, type XYChartData } from '../types/xy-chart.js';
import {
  isInfiniteZoomEnabledFor,
  isPanEnabledFor,
  isZoomEnabledFor,
} from '../utils/axis-interactions.js';
import { calculateAxesDomains } from '../utils/calculate-axes-domains.js';
import { combineDataWithCorrespondingSeries } from '../utils/combine-data-with-corresponding-series.js';
import { combineSeriesWithAxes } from '../utils/combine-series-with-axes.js';
import { getCenterDate } from '../utils/get-center-date.js';
import { getDotDatapoint, getRectDatapoint } from '../utils/get-datapoints.js';
import { getLegendType } from '../utils/get-legend-type.js';
import { getValueScaleDomain } from '../utils/get-value-scale-domain.js';
import { isDotSeries, isRectSeries } from '../utils/type-guards.js';

interface XYChartChartDataProviderProps {
  seriesVariantsProps: XYChartVariantsInternal[];
  data: XYChartData[];
}

/**
 * A hook to process chart data and configure axes for an XY chart.
 *
 * @param seriesVariantsProps - The array of series variant properties.
 * @param data - The array of chart data.
 * @returns - An object containing axes, series with data points, rect data points to metadata mapping, axis ID to scale domain mapping, and series ID to value scale domain mapping.
 * @returns axes - The array of valid axes configurations.
 * @returns seriesWithDatapoints - The array of series with data points.
 * @returns rectSeriesToMetadata - A mapping of rect series to their metadata.
 * @returns axisIdToScaleDomain - A mapping of axis IDs to their scale domains.
 * @returns seriesIdToValueScaleDomain - A mapping of series IDs to their value scale domains.
 */
export function useXYChartDataProvider(
  seriesVariantsProps: XYChartVariantsInternal[],
  data: XYChartData[],
) {
  const axes = useAxesConfig();
  const colorPalette = useColorPalette();

  const seriesIdToValueScaleDomain: RectSeriesIdToValueScaleDomain = new Map();
  const axisIdToScaleDomain: AxisIdToScaleDomain = new Map();
  const rectSeriesToMetadata: RectSeriesToMetadata = new WeakMap();
  const dotSeriesToMetadata: DotSeriesToMetadata = new WeakMap();
  const datapointMetadataMap: WeakMapDatapoints = new WeakMap();

  const seriesWithAxes = combineSeriesWithAxes(seriesVariantsProps, axes);
  const dataWithSeries = combineDataWithCorrespondingSeries(
    data,
    seriesWithAxes,
  );

  const rectSeriesWithDatapoints: RectSeriesWithDatapoints[] = [];
  const dotSeriesWithDatapoints: DotSeriesWithDatapoints[] = [];

  const seriesMap = new Map();

  for (const { data, series } of dataWithSeries) {
    for (const datum of data) {
      for (let i = 0; i < series.length; i++) {
        const { seriesProps, xAxis, yAxis, paths } = series[i];
        let currentSeries = seriesMap.get(seriesProps.id);
        const isRect = isRectSeries(seriesProps);
        const isDot = isDotSeries(seriesProps);

        let datapoint;

        if (isRect) {
          datapoint = getRectDatapoint(datum, paths, xAxis, yAxis);

          if (!datapoint) {
            continue;
          }

          if (!currentSeries) {
            currentSeries = {
              id: seriesProps.id,
              type: seriesProps.type,
              data: [],
              xAxisId: xAxis.id,
              yAxisId: yAxis.id,
            };
            seriesMap.set(seriesProps.id, currentSeries);

            rectSeriesWithDatapoints.push(currentSeries);

            const validatedColorPalette =
              seriesProps.colorPalette &&
              (isColoredRangeArray(seriesProps.colorPalette) ||
                isCustomColorPalette(seriesProps.colorPalette) ||
                isValidColorPalette(seriesProps.colorPalette))
                ? seriesProps.colorPalette
                : colorPalette;

            rectSeriesToMetadata.set(currentSeries, {
              xAxis,
              yAxis,
              actions: seriesProps.actions,
              valueFormatter: seriesProps.valueFormatter,
              valueUnit: seriesProps.valueUnit,
              colorPalette: validatedColorPalette,
              valueAccessor: seriesProps.valueAccessor,
              valueType: undefined,
              valueAccessorLabel: seriesProps.valueAccessorLabel,
              valueMin: seriesProps.valueMin,
              valueMax: seriesProps.valueMax,
            });
          }

          const datapointMetadata: DatapointMetadata = {
            isGap: true,
          };

          if (
            datapoint.value !== null &&
            datapoint.value !== undefined &&
            !Number.isNaN(datapoint.value)
          ) {
            const valueDomain = getValueScaleDomain(
              datapoint.value,
              seriesProps.id,
              seriesIdToValueScaleDomain,
            );

            seriesIdToValueScaleDomain.set(seriesProps.id, valueDomain);

            datapointMetadata.isGap = false;
            const tempMetadata = safeGet(rectSeriesToMetadata, currentSeries);

            if (!tempMetadata.valueType) {
              tempMetadata.valueType = getLegendType(datapoint.value);
            }
          }

          currentSeries.data.push(datapoint);

          const { xDomain, yDomain } = calculateAxesDomains(
            xAxis,
            yAxis,
            datapoint,
            axisIdToScaleDomain,
          );

          axisIdToScaleDomain.set(xAxis.id, xDomain);
          axisIdToScaleDomain.set(yAxis.id, yDomain);
          datapointMetadataMap.set(datapoint, datapointMetadata);
        } else if (isDot) {
          datapoint = getDotDatapoint(datum, paths, xAxis, yAxis);

          if (!datapoint) {
            continue;
          }

          if (!currentSeries) {
            currentSeries = {
              id: seriesProps.id,
              type: seriesProps.type,
              data: [],
              xAxisId: xAxis.id,
              yAxisId: yAxis.id,
            };
            seriesMap.set(seriesProps.id, currentSeries);

            dotSeriesWithDatapoints.push(currentSeries);

            const validatedColorPalette =
              seriesProps.colorPalette &&
              (isColoredRangeArray(seriesProps.colorPalette) ||
                isCustomColorPalette(seriesProps.colorPalette) ||
                isValidColorPalette(seriesProps.colorPalette))
                ? seriesProps.colorPalette
                : colorPalette;

            const color = seriesProps.color
              ? { Default: getValidColor(seriesProps.color) }
              : getColorPalette(
                  Array.isArray(seriesProps.name)
                    ? seriesProps.name[0]
                    : seriesProps.name,
                  i,
                  // @ts-expect-error - todo
                  validatedColorPalette,
                );

            dotSeriesToMetadata.set(currentSeries, {
              xAxis,
              yAxis,
              actions: seriesProps.actions,
              colorPalette: validatedColorPalette,
              color,
              name: seriesProps.name,
              shape: seriesProps.shape,
            });
          }

          const datapointMetadata: DatapointMetadata = {
            isGap: false,
          };

          datapointMetadataMap.set(datapoint, datapointMetadata);
          currentSeries.data.push(datapoint);

          const calcPoint = { x0: datapoint.x0, y0: datapoint.y0 };

          if (xAxis.type === TIME_SCALE_TYPE) {
            calcPoint.x0 = getCenterDate(datapoint.x0 as Date, datapoint.x1);
          }
          if (yAxis.type === TIME_SCALE_TYPE) {
            calcPoint.y0 = getCenterDate(datapoint.y0 as Date, datapoint.y1);
          }
          const { xDomain, yDomain } = calculateAxesDomains(
            xAxis,
            yAxis,
            calcPoint,
            axisIdToScaleDomain,
          );

          axisIdToScaleDomain.set(xAxis.id, xDomain);
          axisIdToScaleDomain.set(yAxis.id, yDomain);
        }
      }
    }
  }

  const validAxes = axes.filter((axis) => axisIdToScaleDomain.has(axis.id));

  return {
    axesMetadata: {
      isZoomEnabled: !!validAxes.find(isZoomEnabledFor),
      isPanEnabled: !!validAxes.find(isPanEnabledFor),
      isInfinite: !!validAxes.find(isInfiniteZoomEnabledFor),
    },
    axes: validAxes,
    rectSeriesWithDatapoints,
    rectSeriesToMetadata,
    axisIdToScaleDomain,
    seriesIdToValueScaleDomain,
    dotSeriesWithDatapoints,
    dotSeriesToMetadata,
    datapointMetadataMap,
  };
}

export const XYChartChartDataProvider = ({
  seriesVariantsProps,
  data,
  children,
}: PropsWithChildren<XYChartChartDataProviderProps>) => {
  const {
    axes,
    rectSeriesWithDatapoints,
    rectSeriesToMetadata,
    axisIdToScaleDomain,
    seriesIdToValueScaleDomain,
    dotSeriesWithDatapoints,
    dotSeriesToMetadata,
    axesMetadata,
    datapointMetadataMap,
  } = useXYChartDataProvider(seriesVariantsProps, data);

  return (
    <XYChartDataContext.Provider
      value={{
        axesMetadata,
        axes,
        seriesWithDatapoints: [
          ...rectSeriesWithDatapoints,
          ...dotSeriesWithDatapoints,
        ],
        axisIdToScaleDomain,
        datapointMetadataMap,
      }}
    >
      <XYChartRectDataContext.Provider
        value={{
          seriesWithDatapoints: rectSeriesWithDatapoints,
          seriesIdToValueScaleDomain,
          seriesToMetadata: rectSeriesToMetadata,
        }}
      >
        <XYChartDotSeriesDataContext.Provider
          value={{
            seriesWithDatapoints: dotSeriesWithDatapoints,
            seriesToMetadata: dotSeriesToMetadata,
          }}
        >
          {children}
        </XYChartDotSeriesDataContext.Provider>
      </XYChartRectDataContext.Provider>
    </XYChartDataContext.Provider>
  );
};

XYChartChartDataProvider['displayName'] = 'XYChartChartDataProvider';
